import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supportApi } from '../views/customer/support/hooks/supportApi';

interface SupportUnreadContextType {
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const SupportUnreadContext = createContext<SupportUnreadContextType | undefined>(undefined);

export const SupportUnreadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await supportApi.getUnreadCount();
      console.log("Context - Full API response:", response);
      console.log("Context - Response data:", response.data);
      
      // Set unread count from API response
      if (response.data?.data !== undefined) {
        setUnreadCount(response.data.data);
      } else {
        console.warn("Context - Unexpected response structure:", response.data);
        setUnreadCount(0);
      }
    } catch (err: any) {
      console.error("Context - Failed to fetch unread messages count:", err);
      setError(err.message || "Failed to fetch unread count");
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load unread count on mount
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const value = {
    unreadCount,
    isLoading,
    error,
    refetch: fetchUnreadCount,
  };

  return (
    <SupportUnreadContext.Provider value={value}>
      {children}
    </SupportUnreadContext.Provider>
  );
};

export const useSupportUnreadContext = (): SupportUnreadContextType => {
  const context = useContext(SupportUnreadContext);
  if (context === undefined) {
    throw new Error('useSupportUnreadContext must be used within a SupportUnreadProvider');
  }
  return context;
};
